<template>
  <Dialog v-model="dialogVisible" :title="dialogTitle" width="1100">
    <el-form
      ref="formRef"
      v-loading="formLoading"
      :model="formData"
      :rules="formRules"
      label-width="110px"
    >
      <!-- ========== 第 1 组：归属（选择器） ========== -->
      <el-row>
        <el-col :span="12">
          <el-form-item label="客户名称" prop="customerId">
            <el-select
              v-model="formData.customerId"
              :disabled="formType !== 'create'"
              class="w-1/1"
              filterable
              placeholder="请选择客户"
              @change="handleCustomerChange"
            >
              <el-option
                v-for="item in customerList"
                :key="item.id"
                :label="item.name"
                :value="item.id"
              />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <!-- ★ 原型表单里没有「合同名称」，只有只读的「合同编号」+ 一个「订单编号」下拉。
               老系统是「选订单 → 带出合同」，但本系统订单与合同无数据级关联
               （crm_order.contract_no 是字符串、当前 0 行有值），故保留「客户 → 合同」两级选择。 -->
          <el-form-item label="合同名称" prop="contractId">
            <el-select
              v-model="formData.contractId"
              :disabled="formType !== 'create' || !formData.customerId"
              class="w-1/1"
              filterable
              :placeholder="formData.customerId ? '请选择合同' : '请先选择客户'"
              @change="handleContractChange"
            >
              <!-- 2026-09-20 业务调整：回款不再要求合同「审核通过」，本客户下全部合同均可选。
                   非「审核通过」的合同在名称后追加当前状态，仅作提示，不阻止选择。 -->
              <el-option
                v-for="data in contractList"
                :key="data.id"
                :label="formatContractOptionLabel(data)"
                :value="data.id!"
              />
              <template #empty>
                <div class="text-12px text-gray-400 p-10px text-center">
                  {{ formData.customerId ? '该客户下暂无合同' : '请先选择客户' }}
                </div>
              </template>
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>

      <!-- ========== 第 2 组：合同信息（★ 全部只读带出，按原型顺序） ==========
           数据源是所选合同（crm_contract）+ 后端聚合的首笔回款日期。
           ★ 刻意不做成可编辑、也不在 crm_receivable 上冗余：
             与「2026-09-21 回款列表按原型改造」的口径完全一致（列表这 11 列也是读 row.contract.xxx），
             避免「表单改了、列表没变」或「改回款把合同改了」。
           映射口径：生效日期←合同开始时间；交付日期←合同结束时间；首款日期←该合同首笔回款日期。 -->
      <el-row>
        <el-col :span="12">
          <el-form-item label="订单编号">
            <el-input :model-value="contract?.orderNo || '-'" disabled />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="合同编号">
            <el-input :model-value="contract?.no || '-'" disabled />
          </el-form-item>
        </el-col>
      </el-row>
      <el-row>
        <el-col :span="12">
          <el-form-item label="合同类型">
            <el-input
              :model-value="fmtDict(DICT_TYPE.CRM_CONTRACT_TYPE, contract?.contractType)"
              disabled
            />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="签约日期">
            <el-input :model-value="fmtDate(contract?.signDate)" disabled />
          </el-form-item>
        </el-col>
      </el-row>
      <el-row>
        <el-col :span="12">
          <el-form-item label="首款日期">
            <el-input :model-value="fmtDate(formData.firstReturnTime)" disabled />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="生效日期">
            <el-input :model-value="fmtDate(contract?.startTime)" disabled />
          </el-form-item>
        </el-col>
      </el-row>
      <el-row>
        <el-col :span="12">
          <el-form-item label="交付日期">
            <el-input :model-value="fmtDate(contract?.endTime)" disabled />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="合同金额">
            <el-input :model-value="fmtPrice(contract?.totalPrice)" disabled />
          </el-form-item>
        </el-col>
      </el-row>
      <el-row>
        <el-col :span="12">
          <el-form-item label="收/付款">
            <el-input
              :model-value="
                fmtDict(DICT_TYPE.CRM_CONTRACT_PAYMENT_DIRECTION, contract?.paymentDirection)
              "
              disabled
            />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="付款方式">
            <el-input
              :model-value="fmtDict(DICT_TYPE.CRM_CONTRACT_PAYMENT_METHOD, contract?.paymentMethod)"
              disabled
            />
          </el-form-item>
        </el-col>
      </el-row>
      <el-row>
        <el-col :span="12">
          <el-form-item label="付款分期">
            <el-input
              :model-value="fmtDict(DICT_TYPE.CRM_CONTRACT_INSTALLMENT, contract?.installment)"
              disabled
            />
          </el-form-item>
        </el-col>
      </el-row>
      <el-row>
        <el-col :span="24">
          <el-form-item label="说明">
            <el-input
              :model-value="contract?.description || '-'"
              :rows="2"
              disabled
              type="textarea"
            />
          </el-form-item>
        </el-col>
      </el-row>

      <!-- ========== 第 3 组：回款信息（可编辑） ========== -->
      <el-row>
        <el-col :span="12">
          <el-form-item label="回款编号" prop="no">
            <el-input v-model="formData.no" disabled placeholder="保存时自动生成" />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="负责人" prop="ownerUserId">
            <el-select
              v-model="formData.ownerUserId"
              :disabled="formType !== 'create'"
              class="w-1/1"
            >
              <el-option
                v-for="item in userOptions"
                :key="item.id"
                :label="item.nickname"
                :value="item.id"
              />
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>
      <el-row>
        <el-col :span="12">
          <el-form-item label="回款期数" prop="planId">
            <el-select
              v-model="formData.planId"
              :disabled="formType !== 'create' || !formData.contractId"
              class="!w-1/1"
              placeholder="请选择回款期数"
              @change="handleReceivablePlanChange"
            >
              <el-option
                v-for="data in receivablePlanList"
                :key="data.id"
                :disabled="!!data.receivableId"
                :label="'第 ' + data.period + ' 期'"
                :value="data.id!"
              />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="回款方式" prop="returnType">
            <el-select v-model="formData.returnType" class="w-1/1" placeholder="请选择回款方式">
              <el-option
                v-for="dict in getIntDictOptions(DICT_TYPE.CRM_RECEIVABLE_RETURN_TYPE)"
                :key="dict.value"
                :label="dict.label"
                :value="dict.value"
              />
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>
      <el-row>
        <el-col :span="12">
          <el-form-item label="回款金额" prop="price">
            <el-input-number
              v-model="formData.price"
              :min="0.01"
              :precision="2"
              class="!w-100%"
              controls-position="right"
              placeholder="请输入回款金额"
            />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="回款日期" prop="returnTime">
            <el-date-picker
              v-model="formData.returnTime"
              class="!w-100%"
              placeholder="选择回款日期"
              type="date"
              value-format="x"
            />
          </el-form-item>
        </el-col>
      </el-row>
      <el-row>
        <el-col :span="12">
          <!-- ★ 原型「是否收款付款」下拉（默认「否」）；字典 crm_receivable_received_flag（1 是 / 0 否） -->
          <el-form-item label="是否收款付款" prop="receivedFlag">
            <el-select v-model="formData.receivedFlag" class="w-1/1" placeholder="请选择">
              <el-option
                v-for="dict in getIntDictOptions(DICT_TYPE.CRM_RECEIVABLE_RECEIVED_FLAG)"
                :key="dict.value"
                :label="dict.label"
                :value="dict.value"
              />
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>
      <el-row>
        <el-col :span="24">
          <!-- ★ 原型「相关文件」（多行文本 + 「+添加文件」）。归属回款自身（不是合同的相关文件）：
               收付款单上通常要传收款凭证等自有附件。
               合同是业务凭据，不属于运营素材，一律直接上传，不走素材库。 -->
          <el-form-item label="相关文件" prop="relatedFiles">
            <UploadFile
              v-model="formData.relatedFiles"
              :use-material-library="false"
              class="min-w-80px"
            />
          </el-form-item>
        </el-col>
      </el-row>
      <el-row>
        <el-col :span="24">
          <el-form-item label="备注" prop="remark">
            <el-input v-model="formData.remark" placeholder="请输入备注" type="textarea" />
          </el-form-item>
        </el-col>
      </el-row>
    </el-form>
    <template #footer>
      <el-button :disabled="formLoading" type="primary" @click="submitForm">确 定</el-button>
      <el-button @click="dialogVisible = false">取 消</el-button>
    </template>
  </Dialog>
</template>
<script lang="ts" setup>
import * as ReceivablePlanApi from '@/api/crm/receivable/plan'
import * as ReceivableApi from '@/api/crm/receivable'
import { ReceivableVO } from '@/api/crm/receivable'
import * as UserApi from '@/api/system/user'
import * as CustomerApi from '@/api/crm/customer'
import * as ContractApi from '@/api/crm/contract'
import { getCurrentUserId } from '@/utils/auth'
import { DICT_TYPE, getDictLabel, getIntDictOptions } from '@/utils/dict'
import { erpPriceInputFormatter } from '@/utils'
import { formatDate } from '@/utils/formatTime'

const { t } = useI18n() // 国际化
const message = useMessage() // 消息弹窗
const userOptions = ref<UserApi.UserVO[]>([]) // 用户列表
const dialogVisible = ref(false) // 弹窗的是否展示
const dialogTitle = ref('') // 弹窗的标题
const formLoading = ref(false) // 表单的加载中：1）修改时的数据加载；2）提交的按钮禁用
const formType = ref('') // 表单的类型：create - 新增；update - 修改
const formData = ref<ReceivableApi.ReceivableVO>({} as ReceivableApi.ReceivableVO)
/**
 * 当前选中的合同（用于第 2 组「合同信息」只读带出）
 *
 * ★ 新增时用 ContractApi.getContract(id) 取完整对象（下拉用的 getContractSimpleList 只有 id/name）；
 *   修改时直接用后端 get 接口塞进 data.contract 的完整对象，免一次请求。
 */
const contract = ref<ReceivableApi.ReceivableVO['contract']>()
const formRules = reactive({
  customerId: [{ required: true, message: '客户不能为空', trigger: 'blur' }],
  contractId: [{ required: true, message: '合同不能为空', trigger: 'blur' }],
  returnTime: [{ required: true, message: '回款日期不能为空', trigger: 'blur' }],
  price: [{ required: true, message: '回款金额不能为空', trigger: 'blur' }]
})
const formRef = ref() // 表单 Ref
const customerList = ref<CustomerApi.CustomerVO[]>([]) // 客户列表
const contractList = ref<ContractApi.ContractVO[]>([]) // 合同列表
const receivablePlanList = ref<ReceivablePlanApi.ReceivablePlanVO[]>([]) // 回款计划列表

/** 合同审核状态：20 = 审核通过（对应后端 CrmAuditStatusEnum.APPROVE） */
const CRM_AUDIT_STATUS_APPROVE = 20
/** 审核状态的展示文案（与 CrmAuditStatusEnum 一一对应，仅用于下拉项说明） */
const AUDIT_STATUS_LABEL: Record<number, string> = {
  0: '未提交',
  10: '审批中',
  20: '审核通过',
  30: '审核不通过',
  40: '已取消'
}

/** 合同下拉项的显示文案：非「审核通过」的合同追加状态，仅作提示，不影响可选性 */
const formatContractOptionLabel = (contract: ContractApi.ContractVO) => {
  if (contract.auditStatus === CRM_AUDIT_STATUS_APPROVE) {
    return contract.name
  }
  const label = AUDIT_STATUS_LABEL[contract.auditStatus] || '未知状态'
  return `${contract.name}（${label}）`
}

/** 只读字段的日期展示：空值统一 '-'（不能直接交给 formatDate，它对空值的兜底不可控） */
const fmtDate = (v?: Date | string) => (v ? formatDate(v, 'YYYY-MM-DD') : '-')
/** 只读字段的字典展示：空值或字典未匹配都显示 '-' */
const fmtDict = (dictType: string, v?: string | number) =>
  v === null || v === undefined || v === '' ? '-' : getDictLabel(dictType, v) || '-'
/** 只读字段的金额展示 */
const fmtPrice = (v?: number | string) =>
  v === null || v === undefined || v === '' ? '-' : erpPriceInputFormatter(v)

/**
 * 打开弹窗
 *
 * @param type 表单类型：create-新增；update-修改
 * @param id 回款编号，修改时必传
 * @param receivablePlan 从「回款计划」创建回款时传入的计划对象（会带出期数、金额、回款方式）
 * @param preset 从客户详情/合同详情等列表上下文创建时传入的预填客户、合同（仅 create 生效）
 *
 * ⚠️ receivablePlan 与 preset 必须分开传：早期版本把 { customerId, contractId } 当作
 *    receivablePlan 传进来，导致「修改」时也会走该分支，把已经查出来的合同字段覆盖掉。
 */
const open = async (
  type: string,
  id?: number,
  receivablePlan?: ReceivablePlanApi.ReceivablePlanVO,
  preset?: { customerId?: number; contractId?: number }
) => {
  dialogVisible.value = true
  dialogTitle.value = t('action.' + type)
  formType.value = type
  resetForm()
  // 修改时，设置数据
  if (id) {
    formLoading.value = true
    try {
      const data = (await ReceivableApi.getReceivable(id)) as ReceivableVO
      formData.value = data
      await handleCustomerChange(data.customerId!)
      formData.value.contractId = data?.contract?.id
      // ★ 第 2 组只读字段需要完整合同对象。修改时后端 get 接口已把完整 CrmContractRespVO
      //   塞进 data.contract（见 CrmReceivableController#buildReceivableDetailList），直接用，免一次请求。
      contract.value = data?.contract
      // 回款期数下拉补齐选项：只取列表、不回写 price，
      // 否则会把已保存的回款金额覆盖成「合同剩余可回款金额」。
      if (data?.customerId && data?.contract?.id) {
        receivablePlanList.value = await ReceivablePlanApi.getReceivablePlanSimpleList(
          data.customerId,
          data.contract.id
        )
      }
    } finally {
      formLoading.value = false
    }
  }
  // 获得用户列表
  userOptions.value = await UserApi.getSimpleUserList()
  // 获得客户列表
  customerList.value = await CustomerApi.getCustomerSimpleList()
  // 默认新建时选中自己
  if (formType.value === 'create') {
    formData.value.ownerUserId = getCurrentUserId()
  }
  // 从回款计划创建回款
  if (receivablePlan) {
    formData.value.customerId = receivablePlan.customerId
    await handleCustomerChange(receivablePlan.customerId)
    formData.value.contractId = receivablePlan.contractId
    if (receivablePlan.contractId) {
      await handleContractChange(receivablePlan.contractId)
    }
    if (receivablePlan.id) {
      formData.value.planId = receivablePlan.id
      formData.value.price = receivablePlan.price
      formData.value.returnType = receivablePlan.returnType
    }
  }
  // 从客户详情/合同详情等列表上下文预填（仅新增生效）
  if (formType.value === 'create' && preset?.customerId) {
    formData.value.customerId = preset.customerId
    await handleCustomerChange(preset.customerId)
    // 2026-09-20 业务调整：合同不再要求「审核通过」，只要在本客户合同列表里就直接预填
    const selected = contractList.value.find((item) => item.id === preset.contractId)
    if (selected) {
      formData.value.contractId = selected.id
      await handleContractChange(selected.id)
    }
  }
}
defineExpose({ open }) // 提供 open 方法，用于打开弹窗

/** 提交表单 */
const emit = defineEmits(['success']) // 定义 success 事件，用于操作成功后的回调
const submitForm = async () => {
  // 校验表单
  if (!formRef) return
  const valid = await formRef.value.validate()
  if (!valid) return
  // 提交请求
  formLoading.value = true
  try {
    const data = formData.value as unknown as ReceivableApi.ReceivableVO
    if (formType.value === 'create') {
      await ReceivableApi.createReceivable(data)
      message.success(t('common.createSuccess'))
    } else {
      await ReceivableApi.updateReceivable(data)
      message.success(t('common.updateSuccess'))
    }
    dialogVisible.value = false
    // 发送操作成功的事件
    emit('success')
  } finally {
    formLoading.value = false
  }
}

/** 重置表单 */
const resetForm = () => {
  // ★ 「是否收款付款」给默认值 0（否），与原型下拉的默认态一致
  formData.value = { receivedFlag: 0 } as ReceivableApi.ReceivableVO
  contract.value = undefined
  contractList.value = []
  receivablePlanList.value = []
  formRef.value?.resetFields()
}

/** 处理切换客户 */
const handleCustomerChange = async (customerId: number) => {
  // 重置合同编号
  formData.value.contractId = undefined
  // 重置已带出的合同信息（原型第 2 组只读字段）
  contract.value = undefined
  // 获得合同列表
  if (customerId) {
    contractList.value = []
    contractList.value = await ContractApi.getContractSimpleList(customerId)
  }
}

/** 处理切换合同 */
const handleContractChange = async (contractId: number) => {
  // 重置回款计划编号
  formData.value.planId = undefined
  contract.value = undefined
  if (contractId) {
    // 获得回款计划列表
    receivablePlanList.value = []
    receivablePlanList.value = await ReceivablePlanApi.getReceivablePlanSimpleList(
      formData.value.customerId!,
      contractId
    )
    // ★ 拉完整合同对象：下拉用的 getContractSimpleList 只返回 id/name，
    //   第 2 组只读字段（订单编号/合同类型/签约日期/生效·交付日期/合同金额/收付款/付款方式/付款分期/说明）
    //   需要完整对象才能带出。
    contract.value = await ContractApi.getContract(contractId)
    // 设置金额
    const selected = contractList.value.find((item) => item.id === contractId)
    if (selected) {
      formData.value.price = selected.totalPrice - selected.totalReceivablePrice
    }
  }
}

/** 处理切换回款计划 */
const handleReceivablePlanChange = (planId: number) => {
  if (!planId) {
    return
  }
  const receivablePlan = receivablePlanList.value.find((item) => item.id === planId)
  if (!receivablePlan) {
    return
  }
  formData.value.price = receivablePlan.price
  formData.value.returnType = receivablePlan.returnType
}
</script>
